import { CurecallList, CurecallModalContainer, LoadingSpinner } from '@honestica/curecall-ui'
import { TPatient } from 'src/types/patient'
import { useQuery } from '@tanstack/react-query'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { PatientService } from 'src/services/patient.service'
import { EUserRole, EUserTitle } from 'src/types/user'
import { AxiosResponse } from 'axios'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { getInternationalSortableDateTimeString } from 'src/utils/date-formatter.util'
import * as Sentry from '@sentry/react'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'

type TPatientAuditModalProps = {
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname'>
  isOpen: boolean
  onToggle: VoidFunction
}

export enum EAPIMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
  UNKNOWN = 'UNKNOWN',
}

export enum EAuthorType {
  USER = 'USER',
  PATIENT = 'PATIENT',
  SYSTEM = 'SYSTEM',
  ANONYMOUS = 'ANONYMOUS',
}

export type TPatientAuditData = {
  id: number
  authorType: EAuthorType
  AuthorUserId: number
  apiMethod: EAPIMethod
  apiUrl: string
  apiResponseCode: number
  createdAt: Date
  AuthorUser: {
    id: number
    title: EUserTitle
    firstname: string
    lastname: string
    role: EUserRole
  }
}

/**
 * Retrieves the action text corresponding to the provided API method and URL.
 *
 * @param {TFunction} t - The translation function.
 * @param {EAPIMethod} apiMethod - The API method.
 * @param {string} apiUrl - The API URL.
 * @returns {string|null} - The action corresponding to the API method and URL, or null if no action is found.
 */
const getAction = (t: TFunction, apiMethod: EAPIMethod, apiUrl: string): string | null => {
  const genericUrl = apiUrl.replaceAll(/\/[0-9]+/g, '/_').replace(/\/$/, '')
  const apiAction = `${apiMethod}:${genericUrl}`
  switch (apiAction) {
    case 'GET:/v2/projects/_/patients/_':
      return t('components.patient.audit.actions.viewed')
    case 'PUT:/v2/projects/_/patients/_':
      return t('components.patient.audit.actions.updated-patient')
    case 'POST:/v2/projects/_/patients/_/followups/_/patient-med-events':
      return t('components.patient.audit.actions.updated-followup')
    default:
      return null
  }
}

const formatDate = (date: Date, timezone?: string) => {
  return getInternationalSortableDateTimeString(date, timezone)
}

/**
 * Max audit log number deemed safe before overloading the DOM on a low-end
 * computer
 */
const MAX_SAFE_AUDIT_LOGS_TO_DISPLAY = 1000

export const PatientAuditModal = ({ patient, isOpen, onToggle }: TPatientAuditModalProps) => {
  const { project } = useCurrentProject()
  const { t } = useTranslation()
  const { displayTimezone } = useTimezonePreference()

  const {
    isLoading,
    error,
    data: patientAuditData,
  } = useQuery<any, any, AxiosResponse<TPatientAuditData[]>, any>({
    queryKey: ['patient-audit', project.id, patient.id],
    queryFn: () => PatientService.getPatientAuditData(project.id, patient.id),
  })

  if (isLoading) {
    return (
      <CurecallModalContainer
        closeModalAriaLabel={t('components.patient.audit.modal-close-aria-label')}
        isOpen={isOpen}
        toggle={onToggle}
        dialogClassName="sm:c-w-[750px] xl:c-w-[900px] [&>div]:c-flex [&>div]:c-flex-col"
        title={t('components.patient.audit.modal-title')}
      >
        <LoadingSpinner />
      </CurecallModalContainer>
    )
  }

  if (error || !patientAuditData) {
    return (
      <CurecallModalContainer
        closeModalAriaLabel={t('components.patient.audit.modal-close-aria-label')}
        isOpen={isOpen}
        toggle={onToggle}
        dialogClassName="sm:c-w-[750px] xl:c-w-[900px] [&>div]:c-flex [&>div]:c-flex-col"
        title={t('components.patient.audit.modal-title')}
      >
        <p>{t('components.patient.audit.error-loading-data')}</p>
      </CurecallModalContainer>
    )
  }

  const tableRowsData =
    Array.isArray(patientAuditData.data) && patientAuditData.data.length > 0
      ? patientAuditData.data
          .map((patientAudit) => {
            const action = getAction(t, patientAudit.apiMethod, patientAudit.apiUrl)
            if (!action) {
              return null
            }
            return {
              id: patientAudit.id,
              date: formatDate(patientAudit.createdAt, displayTimezone),
              author: `${patientAudit.AuthorUser?.title} ${patientAudit.AuthorUser?.firstname} 
                      ${patientAudit.AuthorUser?.lastname}`,
              action: action,
            }
          })
          .filter((data) => data !== null)
      : []

  if (tableRowsData.length > MAX_SAFE_AUDIT_LOGS_TO_DISPLAY) {
    Sentry.captureMessage('Too much Audit Logs to display on a single patient', {
      extra: { projectId: project.id, patientId: patient.id },
    })
  }

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('components.patient.audit.modal-close-aria-label')}
      isOpen={isOpen}
      toggle={onToggle}
      dialogClassName="sm:c-w-[750px] xl:c-w-[900px] [&>div]:c-flex [&>div]:c-flex-col"
      title={t('components.patient.audit.modal-title')}
    >
      {tableRowsData.length > 0 ? (
        <>
          {tableRowsData.length > MAX_SAFE_AUDIT_LOGS_TO_DISPLAY && (
            <p>{t('components.patient.audit.max-number-reached', { max: MAX_SAFE_AUDIT_LOGS_TO_DISPLAY })}</p>
          )}
          <CurecallList
            rowsData={tableRowsData as any}
            headersData={[
              { label: t('components.patient.audit.date-column'), key: 'date', isSorted: true },
              { label: t('components.patient.audit.author-column'), key: 'author', isSorted: true },
              { label: t('components.patient.audit.action-column'), key: 'action', isSorted: true },
            ]}
          />
        </>
      ) : (
        <p>{t('components.patient.audit.no-data')}</p>
      )}
    </CurecallModalContainer>
  )
}
